import React, { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, Trash2, FileText, ArrowLeft, Search, CheckCircle2, Send, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { listForms, deleteForm, fetchCustomers } from "@/lib/dashboardApi";
import { TEMPLATES, getTemplate } from "@/lib/formTemplates";
import FormEditor from "@/pages/admin/FormEditor";

const ORANGE = "#F97316";

const STATUS_BADGE = {
  draft: { cls: "bg-slate-100 text-slate-600", label: "Draft" },
  sent: { cls: "bg-amber-100 text-amber-700", label: "Sent" },
  sending: { cls: "bg-slate-100 text-slate-600", label: "Sending" },
  signed: { cls: "bg-green-100 text-green-700", label: "Signed" },
  send_failed: { cls: "bg-red-100 text-red-700", label: "Send failed" },
};

export default function FormsManager() {
  const [view, setView] = useState("list"); // list | pick | customer | edit
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tpl, setTpl] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listForms()); }
    catch { toast.error("Could not load documents."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = async (template) => {
    setTpl(template);
    try { setCustomers(await fetchCustomers()); } catch { setCustomers([]); }
    setView("customer");
  };

  const startEditor = (customer) => { setEditCustomer(customer); setEditing(null); setView("edit"); };

  const openExisting = (form) => {
    setTpl(getTemplate(form.form_type));
    setEditing(form);
    setEditCustomer(form.customer_id ? { id: form.customer_id, name: form.customer_name } : null);
    setView("edit");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    setRows((p) => p.filter((r) => r.id !== id));
    try { await deleteForm(id); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); load(); }
  };

  if (view === "edit") {
    return <FormEditor template={tpl} existing={editing} customer={editCustomer} onBack={() => { setView("list"); load(); }} onSaved={load} />;
  }

  if (view === "customer") {
    return (
      <div data-testid="forms-customer-pick">
        <button onClick={() => setView("pick")} className="flex items-center gap-2 text-slate-600 hover:text-[#F97316] font-medium text-sm mb-5"><ArrowLeft className="w-4 h-4" /> Back</button>
        <h1 className="text-2xl font-bold text-navy mb-1">New {tpl?.name}</h1>
        <p className="text-slate-500 mb-5 text-sm">Attach a customer to pre-fill the document, or start blank.</p>
        <div className="max-w-md space-y-3">
          <button onClick={() => startEditor(null)} data-testid="forms-start-blank" className="w-full flex items-center gap-3 bg-white border border-slate-200 hover:border-[#F97316] rounded-xl p-4 text-left">
            <FilePlus2 className="w-5 h-5 text-[#F97316]" /> <span className="font-semibold text-navy">Start blank</span>
          </button>
          {customers.length > 0 && <p className="text-xs font-bold uppercase text-slate-400 pt-2">Or pick a customer</p>}
          <div className="max-h-80 overflow-y-auto space-y-2">
            {customers.map((c) => (
              <button key={c.id} onClick={() => startEditor(c)} data-testid={`forms-customer-${c.id}`} className="w-full flex items-center justify-between bg-white border border-slate-200 hover:border-[#F97316] rounded-lg px-4 py-3 text-left">
                <span><span className="block font-semibold text-navy text-sm">{c.name}</span><span className="block text-xs text-slate-400">{c.phone || c.email || ""}</span></span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (view === "pick") {
    return (
      <div data-testid="forms-template-pick">
        <button onClick={() => setView("list")} className="flex items-center gap-2 text-slate-600 hover:text-[#F97316] font-medium text-sm mb-5"><ArrowLeft className="w-4 h-4" /> Back to Forms</button>
        <h1 className="text-2xl font-bold text-navy mb-1">New Document</h1>
        <p className="text-slate-500 mb-5 text-sm">Choose a template to get started.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEMPLATES.map((t) => (
            <button key={t.id} onClick={() => openNew(t)} data-testid={`forms-template-${t.id}`} className="bg-white border border-slate-200 hover:border-[#F97316] rounded-xl p-5 text-left transition-colors">
              <FileText className="w-7 h-7 text-[#F97316] mb-3" />
              <h3 className="font-bold text-navy">{t.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{t.blurb}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const filtered = rows.filter((r) => `${r.title} ${r.customer_name} ${r.form_type}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="forms-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy">Forms <span className="text-slate-400 text-lg font-normal">({rows.length})</span></h1>
        <button onClick={() => setView("pick")} className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1.5 hover:opacity-90" style={{ background: ORANGE }} data-testid="forms-new-doc">
          <Plus className="w-4 h-4" /> New Document
        </button>
      </div>
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-cool" data-testid="forms-search" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No documents yet — create your first contract, invoice or estimate.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Document</th>
                  <th className="text-left px-4 py-3 font-bold">Customer</th>
                  <th className="text-left px-4 py-3 font-bold">Type</th>
                  <th className="text-left px-4 py-3 font-bold">Status</th>
                  <th className="text-left px-4 py-3 font-bold">Updated</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const b = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`forms-row-${r.id}`} onClick={() => openExisting(r)}>
                      <td className="px-4 py-3 font-semibold text-slate-800">{r.title || "Untitled"}</td>
                      <td className="px-4 py-3 text-slate-600">{r.customer_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 capitalize">{(r.form_type || "").replace("_", " ")}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${b.cls}`}>{r.signed ? <CheckCircle2 className="w-3 h-3" /> : r.status === "sent" ? <Send className="w-3 h-3" /> : null}{b.label}</span></td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{(r.modified_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`forms-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
